import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { baseUrl, escapeKeyCode } from '#constants';
import { toClipboard } from '#utils';
import { useKey } from '#hooks';
import routes from '#routes';

import Button from '../Button';
import {
  SCard, SHeader, SContent, SLink, SCopyIcon, SFooter,
} from './styled';

const copiedMessageTimeout = 2000;

// state -> i18n text key
const copySteps = Object.freeze({
  none      : 'copy',
  copied    : 'copied',
  copyError : 'copyError',
});

const BoardCreatedWelcome = ({ boardId, onClose }) => {
  const { t } = useTranslation('board');
  const timeoutRef = useRef();
  const [copyStep, setCopyStep] = useState(copySteps.none);

  useKey(escapeKeyCode, onClose);

  const boardUrl = useMemo(
    () => `${baseUrl}${routes.board.replace(':id', boardId)}`,
    [boardId],
  );

  const copyAction = useCallback(
    () => toClipboard(boardUrl)
      .then(() => setCopyStep(copySteps.copied))
      .catch(() => setCopyStep(copySteps.copyError))
      .finally(() => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(
          () => setCopyStep(copySteps.none),
          copiedMessageTimeout,
        );
      }),
    [boardUrl],
  );

  // Clear timeout if it was still active
  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    },
    [timeoutRef],
  );

  return (
    <SCard>
      <SHeader>
        {t('welcome.created.title', { boardId })}
      </SHeader>
      <SContent>
        <div>
          {t('welcome.created.about')}
        </div>
        <SLink id="link">
          {boardUrl}
        </SLink>
        <Button variant="secondary" fullWidth onClick={copyAction}>
          <SCopyIcon />
          {t(`welcome.created.${copyStep}`)}
        </Button>
      </SContent>
      <SFooter>
        <Button variant="secondary" onClick={onClose}>
          {t('welcome.created.accept')}
        </Button>
      </SFooter>
    </SCard>
  );
};

BoardCreatedWelcome.propTypes = {
  boardId : PropTypes.string.isRequired,
  onClose : PropTypes.func.isRequired,
};

export default BoardCreatedWelcome;
